import { useState, useEffect } from "react";
import { Button, Icon, Label, Popup } from "semantic-ui-react";
import { Post, Answer } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { REACTION_TYPES, getReactionByType, socket } from "../../../../utils";
import "./ReactionButton.scss";

const postController = new Post();
const answerController = new Answer();
const MAX_CLUSTER_ICONS = 3;

export function ReactionButton({ targetType, target }) {
  const { accessToken } = useAuth();
  const [reactionsCount, setReactionsCount] = useState(target?.reactionsCount ?? 0);
  const [reactionsByType, setReactionsByType] = useState(target?.reactionsByType ?? {});
  const [myReaction, setMyReaction] = useState(target?.myReaction ?? null);

  useEffect(() => {
    setReactionsCount(target?.reactionsCount ?? 0);
    setReactionsByType(target?.reactionsByType ?? {});
    setMyReaction(target?.myReaction ?? null);
  }, [target?.id, target?.reactionsCount, target?.myReaction, target?.reactionsByType]);

  useEffect(() => {
    const handleReactionUpdate = (payload) => {
      if (payload.targetType === targetType && String(payload.id) === String(target?.id)) {
        setReactionsCount(payload.reactionsCount);
        setReactionsByType(payload.reactionsByType);
      }
    };
    socket.on("reactionUpdate", handleReactionUpdate);
    return () => {
      socket.off("reactionUpdate", handleReactionUpdate);
    };
  }, [targetType, target?.id]);

  const applyReaction = async (type) => {
    const previous = { reactionsCount, reactionsByType, myReaction };

    const nextByType = { ...reactionsByType };
    if (myReaction) {
      nextByType[myReaction] = Math.max((nextByType[myReaction] || 1) - 1, 0);
    }
    let nextCount = reactionsCount;
    let nextMyReaction = null;
    if (myReaction === type) {
      nextCount = Math.max(reactionsCount - 1, 0);
    } else {
      nextByType[type] = (nextByType[type] || 0) + 1;
      nextCount = myReaction ? reactionsCount : reactionsCount + 1;
      nextMyReaction = type;
    }
    setReactionsCount(nextCount);
    setReactionsByType(nextByType);
    setMyReaction(nextMyReaction);

    try {
      const controller = targetType === "post" ? postController : answerController;
      const result = await controller.toggleReaction(accessToken, target.id, type);
      setReactionsCount(result.reactionsCount);
      setReactionsByType(result.reactionsByType);
      setMyReaction(result.type);
    } catch (error) {
      setReactionsCount(previous.reactionsCount);
      setReactionsByType(previous.reactionsByType);
      setMyReaction(previous.myReaction);
      console.error(error);
    }
  };

  const handleMainClick = () => {
    applyReaction(myReaction || "useful");
  };

  const currentReaction = getReactionByType(myReaction);
  const buttonColor = currentReaction?.color || "grey";
  const sortedBreakdown = Object.entries(reactionsByType)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);
  const clusterReactions = sortedBreakdown
    .slice(0, MAX_CLUSTER_ICONS)
    .map(([type]) => getReactionByType(type))
    .filter(Boolean);

  return (
    <div className="reaction-button">
      <Popup
        on="hover"
        hoverable
        mouseEnterDelay={350}
        mouseLeaveDelay={200}
        position="top center"
        trigger={
          <Button
            color={buttonColor}
            size="tiny"
            onClick={handleMainClick}
            className="reaction-button__main"
          >
            {currentReaction ? (
              <currentReaction.icon size={14} className="reaction-button__icon" />
            ) : (
              <Icon name="heart" />
            )}
            {currentReaction ? currentReaction.label : "Reaccionar"}
          </Button>
        }
        content={
          <div className="reaction-button__picker">
            {REACTION_TYPES.map((reaction) => (
              <span
                key={reaction.type}
                className="reaction-button__picker-option"
                title={reaction.label}
                onClick={() => applyReaction(reaction.type)}
              >
                <reaction.icon size={22} color={reaction.hex} />
              </span>
            ))}
          </div>
        }
      />
      <Popup
        on="hover"
        disabled={sortedBreakdown.length === 0}
        position="top center"
        trigger={
          <Label
            basic
            color={buttonColor}
            pointing="left"
            className="reaction-button__count"
          >
            {clusterReactions.length > 0 && (
              <span className="reaction-button__count-icons">
                {clusterReactions.map((reaction) => (
                  <reaction.icon key={reaction.type} size={12} color={reaction.hex} />
                ))}
              </span>
            )}
            {reactionsCount}
          </Label>
        }
        content={
          <div className="reaction-button__breakdown">
            {sortedBreakdown.map(([type, count]) => {
              const reaction = getReactionByType(type);
              if (!reaction) return null;
              return (
                <div key={type} className="reaction-button__breakdown-row">
                  <reaction.icon size={16} color={reaction.hex} />
                  <span className="reaction-button__breakdown-label">{reaction.label}</span>
                  <span className="reaction-button__breakdown-count">{count}</span>
                </div>
              );
            })}
          </div>
        }
      />
    </div>
  );
}
